import { Box, Fade, ListItemText, Menu, MenuItem, SelectChangeEvent, Stack } from "@mui/material";
import { StandardLibraryTable } from "../../components/table/StandardLibraryTable";

import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useLocale } from "../../providers/LocaleProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { TextField } from "../../components/TextField";
import { JobBenchmarkingProvider } from "../../contexts/JobBenchmarkingContext";
import { useStandardsLibrary } from "../../contexts/StandardsLibraryContext";
import { useAppQuery } from "../../hooks/useAppQuery";
import { IJobItem, IJobListResponse } from "../../interfaces/Job";
import SearchIcon from "../../resources/images/icons-search.svg";
import JobBenchmarking from "./JobBenchmarking";
import SettingStandardsLibraryJobDetail from "./SettingStandardsLibraryJobDetail";

const SettingStandardsLibraryJobs = ({ theme }: any) => {
  const { translate } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobs, setRouteState, debounceSearch } = useStandardsLibrary();
  const { isTrial } = useStandardsLibrary();
  const pagingModel = jobs.pagingModel;
  const sortModel = jobs.sortModel;
  const [showJobBenchmarkingDialog, setShowJobBenchmarkingDialog] = useState(false);

  const { data: listJobData, isLoading: isLoadingJob } = useAppQuery<IJobListResponse>(
    `/StandardLibrary/jobs`,
    {
      config: {
        params: {
          pageNumber: pagingModel.page + 1,
          pageSize: pagingModel.pageSize,
          orderBy: sortModel.length > 0 ? `${sortModel[0].field} ${sortModel[0].sort}` : undefined,
          searchText: (jobs.queryText || "").trim()
        }
      }
    }
  );
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [actionTarget, setActionTarget] = useState<null | HTMLElement>(null);
  const [currentRow, setCurrentRow] = useState<any>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const jobId = searchParams.get("id");
    if (jobId) {
      setSelectedJobId(jobId);
      setIsDetailOpen(true);
    }
  }, [location.search]);

  const rows = listJobData?.jobDefinitions?.data || [];
  const rowCount = listJobData?.jobDefinitions?.totalCount || null;

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const value = event.target.value;
    debounceSearch("jobs", value);
  };

  const handleFilterChange = (newFilters: Record<string, any>) => {
    setRouteState("jobs", { filters: newFilters });
  };

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setRouteState("jobs", {
      pagingModel:
        newModel.pageSize !== pagingModel.pageSize
          ? { page: 0, pageSize: newModel.pageSize }
          : newModel
    });
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setRouteState("jobs", { sortModel: newSortModel });
    setRouteState("jobs", { pagingModel: { ...pagingModel, page: 0 } });
  };

  const handleActionMenu = (event: any, row: any) => {
    setActionTarget(event.currentTarget);
    setSelectedJobId(row.id);
    setCurrentRow(row);
  };

  const handleRowClick = (e: any, row: any) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("id", row.id);
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString()
      },
      { replace: true }
    );
  };

  const handleClose = () => {
    setIsDetailOpen(false);
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("id");
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString()
      },
      { replace: true }
    );
  };

  const onCloseActionMenu = () => {
    setActionTarget(null);
  };

  const handleViewDetail = () => {
    setIsDetailOpen(true);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("id", selectedJobId);
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString()
      },
      { replace: true }
    );
    onCloseActionMenu();
  };

  const onCloseImportJobBenchmarkingDialog = () => {
    setShowJobBenchmarkingDialog(false);
    setCurrentRow(undefined);
  };

  const handleShowJobBenchmarking = () => {
    setShowJobBenchmarkingDialog(true);
    onCloseActionMenu();
  };

  return (
    <Box>
      <Stack direction='column' spacing={4}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
          <TextField
            dense={true}
            placeholder={translate("placeholders.searchJob")}
            autoComplete={false}
            leadingIcon={<SearchIcon />}
            fullWidth={true}
            value={jobs.searchText || ""}
            onChange={handleSearchChange}
            sx={{ maxWidth: { md: "300px" }, width: "300px" }}
          />
        </Stack>
        <StandardLibraryTable<IJobItem>
          theme={theme}
          translate={translate}
          selectable={false}
          rows={rows}
          isLoading={isLoadingJob}
          totalCount={rowCount || 0}
          paginationModel={pagingModel}
          onPaginationModelChange={handlePaginationModelChange}
          paginationMode='server'
          onJobSelect={handleRowClick}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingMode='server'
          filterMode='server'
          columns={[
            { type: StandardLibraryTable.columns.name, label: translate("entities.job") },
            { type: StandardLibraryTable.columns.tasks, label: translate("tableColumns.tasks") },
            {
              type: StandardLibraryTable.columns.competencies,
              label: translate("tableColumns.competencies")
            },
            { type: StandardLibraryTable.columns.actions }
          ]}
          dense={true}
          paper={false}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer !important" } }}
          onActionMenu={handleActionMenu}
          keyword={jobs.searchText}
        />
      </Stack>

      <Menu
        open={actionTarget !== null}
        anchorEl={actionTarget}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={onCloseActionMenu}
      >
        <MenuItem onClick={handleViewDetail}>
          <ListItemText>{translate("common.viewDetails")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleShowJobBenchmarking}>
          <ListItemText>{translate("settingsViews.jobBenchmarking.benchmarking")}</ListItemText>
        </MenuItem>
      </Menu>

      <Fade in={isDetailOpen} timeout={300} unmountOnExit>
        <div>
          <SettingStandardsLibraryJobDetail
            theme={theme}
            open={isDetailOpen}
            onClose={handleClose}
            jobId={selectedJobId}
            isTrial={isTrial}
          />
        </div>
      </Fade>

      <Fade in={showJobBenchmarkingDialog} unmountOnExit>
        <div>
          <JobBenchmarkingProvider>
            <JobBenchmarking
              open={showJobBenchmarkingDialog}
              onClose={onCloseImportJobBenchmarkingDialog}
              selectedStandardJob={currentRow}
              theme={theme}
            />
          </JobBenchmarkingProvider>
        </div>
      </Fade>
    </Box>
  );
};

export default SettingStandardsLibraryJobs;
